import { FormText, Cond } from 'Components/FormComponents';
import { FormTel } from 'Components/FormTel';
import { hook, Hooks } from 'Components/Hooks';
import HookedAlert from 'Components/HookedAlert';

import LinkAppUI from './LinkAppUI';

import { ValidatedForm, FormErrorList } from './ValidatedForm';

import {
  LINK_STATE_INACTIVE,
  LINK_STATE_PENDING,
  LINK_STATE_CHOOSE_CONNECT_METHOD,
  LINK_STATE_COLLECT_PHONE_NUMBER,
  LINK_STATE_WEBCALL,
  LINK_STATE_CONNECTING,
  LINK_STATE_CONNECTED,
  LINK_STATE_REGISTER_COMPLETE,
} from './LinkController';

import s from './strings';
import sWebCall from 'WebCall/strings';
import errors from './errors';

export function LinkPage({ ctrl }) {
  const hooks = new Hooks();
  ctrl.on('update', () => hooks.run(ctrl));

  if (ctrl.configErrors.length > 0) {
    return (
      <div class="link-page">
        <div class="info-panel">
          <div class="status-message">
            <h1>{s.ConfigError.title}</h1>
            <ul>
              {ctrl.configErrors.map(err => <li>{err}</li>)}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const ctx = {
    hooks,
    ctrl,
  };

  const imgWrapper = img => {
    return img.cloneNode(true);
  };

  return (
    <div
      class="link-page"
      use:hook={hooks.toggleClass('state', 'is-active', state => state !== LINK_STATE_INACTIVE)}
      use:hook={hooks.toggleClass('hasStatusMessage', 'has-status-message')}
    >
      <div class="info-panel">
        <div class="logo-header-container">
          <Cond test={ctrl.logoHeader}>
            {imgWrapper(ctrl.logoHeader)}
          </Cond>
          <Cond test={ctrl.linkName || ctrl.linkName2}>
            <div
              class="welcome-message-mobile"
              use:hook={hooks.hideEq('state', LINK_STATE_INACTIVE)}
            >
              <WelcomeMessage ctx={ctx} />
            </div>
          </Cond>
        </div>

        <div
          class="welcome-message-container"
          use:hook={hooks.show('isWelcomeMessageVisible')}
        >
          <Cond test={ctrl.linkName || ctrl.linkName2}>
            <div class="welcome-message desktop-only">
              <WelcomeMessage ctx={ctx} />
            </div>
          </Cond>
          <Cond test={ctrl.html.preConferenceTitle}>
            <div
              class="markdown-container"
              use:attachHTMLContent={ctrl.html.preConferenceTitle}
              use:hook={hooks.showEq('state', LINK_STATE_PENDING)}
            />
          </Cond>
          <Cond test={ctrl.html.activeTitleContent}>
            <div
              class="markdown-container"
              use:attachHTMLContent={ctrl.html.activeTitleContent}
              use:hook={hooks.hideEq('state', LINK_STATE_PENDING)}
            />
          </Cond>
          <Cond test={ctrl.html.organizer}>
            <div
              class="markdown-container"
              use:attachHTMLContent={ctrl.html.organizer}
            />
          </Cond>
          <Cond test={ctrl.html.preConferenceBody}>
            <div
              class="markdown-container"
              use:attachHTMLContent={ctrl.html.preConferenceBody}
              use:hook={hooks.showEq('state', LINK_STATE_PENDING)}
            />
          </Cond>
          <Cond test={ctrl.html.activeBodyContent}>
            <div
              class="markdown-container"
              use:attachHTMLContent={ctrl.html.activeBodyContent}
              use:hook={hooks.hideEq('state', LINK_STATE_PENDING)}
            />
          </Cond>
        </div>

        <div
          class="status-container"
          use:hook={hooks.show('hasStatusMessage')}
        >
          <p class="status-message" use:hook={hooks.showEq('state', LINK_STATE_INACTIVE)}>
            <Cond test={ctrl.inactiveMessage}>
              {ctrl.inactiveMessage}
            </Cond>
            <Cond test={!ctrl.inactiveMessage && !ctrl.html.inactiveContent}>
              {s.inactiveMessage}
            </Cond>
          </p>

          <Cond test={ctrl.html.inactiveContent}>
            <div
              class="markdown-container"
              use:hook={hooks.showEq('state', LINK_STATE_INACTIVE)}
              use:attachHTMLContent={ctrl.html.inactiveContent}
            />
          </Cond>

          <p class="status-message" use:hook={hooks.showEq('state', LINK_STATE_CONNECTING)}>
            {s.connectingMessage}
            {' '}
            <ConnectingImage />
          </p>

          <p class="status-message" use:hook={hooks.showEq('state', LINK_STATE_CONNECTED)}>
            {s.connectionMessageLine1}<br />
            {s.connectionMessageLine2}
          </p>
        </div>

        <div use:hook={hooks.showEq('state', LINK_STATE_REGISTER_COMPLETE)}>
          <h1>{s.REGISTER_COMPLETE.registerCompleteTitle}</h1>
          <p>{s.REGISTER_COMPLETE.registerCompleteMessage}</p>
          <br />

          <table class="register-complete-table">
            <tbody>
              <tr>
                <td>{s.REGISTER_COMPLETE.linkName}</td>
                <td>
                  {ctrl.linkName}
                  {ctrl.linkName2 && (
                    <><br />{ctrl.linkName2}</>
                  )}
                </td>
              </tr>
              <Cond test={ctrl.html.organizer}>
                <tr>
                  <td>{s.REGISTER_COMPLETE.organizer}</td>
                  <td><div class="markdown-container" use:attachHTMLContent={ctrl.html.organizer} /></td>
                </tr>
              </Cond>
              <tr>
                <td>{s.REGISTER_COMPLETE.localStartDate}</td>
                <td>{ctrl.localStartDate}</td>
              </tr>
              <tr>
                <td>{s.REGISTER_COMPLETE.localStartTime}</td>
                <td>{ctrl.localStartTime}</td>
              </tr>
              <tr>
                <td>{s.REGISTER_COMPLETE.timezone}</td>
                <td>{ctrl.localTimezoneLabel}</td>
              </tr>
              <tr>
                <td>{s.REGISTER_COMPLETE.duration}</td>
                <td>{ctrl.durationLabel}</td>
              </tr>
              <Cond test={ctrl.html.dialInNumbers}>
                <tr>
                  <td>{s.REGISTER_COMPLETE.dialInNumbers}</td>
                  <td><div class="markdown-container" use:attachHTMLContent={ctrl.html.dialInNumbers} /></td>
                </tr>
              </Cond>

              <tr use:hook={hooks.show('registerData', val => val?.conferenceIDFormatted)}>
                <td>{s.REGISTER_COMPLETE.conferenceID}</td>
                <td use:hook={hooks.text('registerData', val => val?.conferenceIDFormatted)} />
              </tr>
              <tr use:hook={hooks.show('registerData', val => val?.userIDFormatted)}>
                <td>{s.REGISTER_COMPLETE.userID}</td>
                <td use:hook={hooks.text('registerData', val => val?.userIDFormatted)} />
              </tr>
              <tr use:hook={hooks.show('registerData', val => val?.combinedIDFormatted)}>
                <td>{s.REGISTER_COMPLETE.combinedID}</td>
                <td use:hook={hooks.text('registerData', val => val?.combinedIDFormatted)} />
              </tr>
            </tbody>
          </table>
          <Cond test={ctrl.html.confirmation}>
            <div class="markdown-container" use:attachHTMLContent={ctrl.html.confirmation} />
          </Cond>
        </div>

        <Cond test={ctrl.logoFooter}>
          <div class="logo-footer-container">
            {imgWrapper(ctrl.logoFooter)}
          </div>
        </Cond>
      </div>

      <UserInfoForm ctx={ctx} />
      <ChooseConnectMethod ctx={ctx} />
      <PhoneNumberForm ctx={ctx} />
      <Cond test={ctrl.webCallAppController}>
        <WebCall ctx={ctx} />
      </Cond>

      <Cond test={ctrl.logoFooter}>
        <div class="logo-footer-container-mobile">
          {imgWrapper(ctrl.logoFooter)}
        </div>
      </Cond>
    </div>
  );
}

function UserInfoForm({ ctx: { hooks, ctrl } }) {
  const formCtrl = ctrl.userInfoForm;
  const form = new ValidatedForm({ ctrl: formCtrl });

  const submit = e => {
    e.preventDefault();

    formCtrl.submit(form);
  };

  const root = (
    <form
      class="form-panel"
      use:hook={hooks.show('isUserInfoFormVisible')}
      onsubmit={submit}
    >
      <div>
        <ErrorAlert hooks={hooks} />
        <FormErrorList ctrl={formCtrl} icon={false} />
        {ctrl.fields.map(field => <FormText inline form={form} name={field.name} label={field.label} value={field.initialValue} />)}
      </div>
      <div class="btn-list">
        <span class="form-note">{s.requiredFields}</span>
        <button
          type="submit"
          class="btn btn-primary btn-lg"
          use:hook={hooks.text('state', state => state === LINK_STATE_PENDING ? s.preRegisterButtonLabel : s.continue)}
        />
      </div>
      <Cond test={ctrl.organizerEmail}>
        <p class="pre-register-note" use:hook={hooks.showEq('state', LINK_STATE_PENDING)}>
          {s.preRegisterNote.replace('%s', ctrl.organizerEmail)}
        </p>
      </Cond>
    </form>
  );

  form.init();
  formCtrl.validateLoadedFields(form);

  return root;
}

function ChooseConnectMethod({ ctx: { hooks, ctrl }}) {
  return (
    <form
      class="form-panel"
      use:hook={hooks.showEq('state', LINK_STATE_CHOOSE_CONNECT_METHOD)}
    >
      <h2>{s.ConnectionMethods.title}</h2>
      <ErrorAlert hooks={hooks} />
      <div class="btn-list">
        <button
          type="button"
          class="btn btn-primary btn-lg"
          onclick={() => ctrl.collectPhoneNumber()}
          use:hook={hooks.show('enableDialOut')}
        >
          {s.ConnectionMethods.dialOut}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-lg"
          onclick={() => ctrl.chooseDialIn()}
          use:hook={hooks.show('enableDialIn')}
          use:hook={hooks.disabled('isBusy')}
        >
          {s.ConnectionMethods.dialIn}
        </button>
        <button
          type="button"
          class="btn btn-primary btn-lg"
          onclick={() => ctrl.chooseWebCall()}
          use:hook={hooks.show('enableWebCall')}
        >
          {s.ConnectionMethods.webCall}
        </button>
        <BackButton ctrl={ctrl} hooks={hooks} />
      </div>
    </form>
  );
}

function WebCall({ ctx: { hooks, ctrl }}) {
  return (
    <form
      class="form-panel"
      use:hook={hooks.showEq('state', LINK_STATE_WEBCALL)}
    >
      <h2 use:hook={hooks.hide('isWebCallBusy')}>{s.ConnectionMethods.webCall}</h2>
      <ErrorAlert hooks={hooks} />
      <LinkAppUI ctrl={ctrl.webCallAppController} />
      <div class="btn-list" use:hook={hooks.hide('isWebCallBusy')}>
        <button type="button" class="btn btn-primary btn-lg" onclick={() => ctrl.webCallConnect()}>{sWebCall.lblConnect}</button>
        <button type="button" class="btn btn-primary btn-lg" onclick={() => ctrl.webCallSettings()}>{sWebCall.lblSettings}</button>
        <BackButton ctrl={ctrl} hooks={hooks} />
      </div>
    </form>
  );
}

function PhoneNumberForm({ ctx: { hooks, ctrl } }) {
  const formCtrl = ctrl.phoneNumberForm;
  const form = new ValidatedForm({ ctrl: formCtrl });

  const submit = e => {
    e.preventDefault();

    formCtrl.submit(form);
  };

  const root = (
    <form
      class="form-panel"
      use:hook={hooks.showEq('state', LINK_STATE_COLLECT_PHONE_NUMBER)}
      onsubmit={submit}
    >
      <h2>{s.ConnectionMethods.dialOut}</h2>
      <div>
        <ErrorAlert hooks={hooks} />
        <FormErrorList ctrl={formCtrl} icon={false} />
        <FormTel inline form={form} name="phoneNumber" label={s.phoneNumberLabel} value={ctrl.storedPhoneNumber} defaultCountryCodes={[ ctrl.countryCode, ctrl.countryCodeFallback ]} />
      </div>
      <div class="btn-list">
        <button type="submit" class="btn btn-primary btn-lg" use:hook={hooks.disabled('isBusy')}>{s.ConnectionMethods.dialOut}</button>
        <BackButton ctrl={ctrl} hooks={hooks} />
      </div>
    </form>
  );

  form.init();
  formCtrl.validateLoadedFields(form);

  return root;
}

function WelcomeMessage({ ctx: { ctrl } }) {
  return (
    <p>
      {s.welcomeMessage}
      {ctrl.linkName && (
        <><br />{ctrl.linkName}</>
      )}
      {ctrl.linkName2 && (
        <><br />{ctrl.linkName2}</>
      )}
    </p>
  );
}

function ConnectingImage() {
  return (
    <svg
      class="connecting-image"
      x="0px"
      y="0px"
      viewBox="0 0 50 31.25"
      height="31.25"
      width="50"
      preserveAspectRatio="xMidYMid meet"
    >
      <path
        class="connecting-image-track"
        stroke-width="4"
        fill="none"
        pathlength="100"
        d="M0.625 21.5 h10.25 l3.75 -5.875 l7.375 15 l9.75 -30 l7.375 20.875 v0 h10.25"
      />
      <path
        class="connecting-image-car"
        stroke-width="4"
        fill="none"
        pathlength="100"
        d="M0.625 21.5 h10.25 l3.75 -5.875 l7.375 15 l9.75 -30 l7.375 20.875 v0 h10.25"
      />
    </svg>
  );
}

function ErrorAlert({ hooks }) {
  return (
    <HookedAlert hooks={hooks} showProp="errorCode" textContentProp="errorCode" textContentTransform={val => errors[val]} />
  );
}

function BackButton({ ctrl, hooks }) {
  return <button type="button" class="btn btn-primary btn-lg" onclick={() => ctrl.back()} use:hook={hooks.show('isBackAvailable')}>{s.backButtonLabel}</button>;
}

function attachHTMLContent(el, html) {
  el.innerHTML = html();

  // set <a> target attributes to _blank so links open in a new tab
  el.querySelectorAll('a').forEach(a => {
    a.target = '_blank';
  });
}
